<template>
    <b-collapse
        :id="well._id"
        class="edit-clinic-gardner-dialog"
        @shown="addClickListener"
        @hide="removeClickListener"
    >
        <b-card class="edit-gardner">
            <div class="wrapper">
                <b-dropdown class="inner-dropdown" block menu-class="w-100">
                    <template #button-content>
                        <div class="gardner-select">
                            <span>{{ model.expansion || '-' }}</span>
                            <i class="fa fa-chevron-down select-button" />
                        </div>
                        <span class="dropdown-label">{{ $translate('expansion') }}</span>
                    </template>
                    <b-dropdown-item
                        v-for="value in expansionValues"
                        :key="value"
                        href="#"
                        @click="changeValueByKey('expansion', value)"
                    >
                        {{ value || '-' }}
                    </b-dropdown-item>
                </b-dropdown>

                <b-dropdown class="inner-dropdown" block menu-class="w-100">
                    <template #button-content>
                        <div class="gardner-select">
                            <span>{{ model.icm || '-' }}</span>
                            <i class="fa fa-chevron-down select-button" />
                        </div>
                        <span class="dropdown-label">{{ $translate('icm') }}</span>
                    </template>
                    <b-dropdown-item
                        v-for="value in icmValues"
                        :key="value"
                        href="#"
                        @click="changeValueByKey('icm', value)"
                    >
                        {{ value || '-' }}
                    </b-dropdown-item>
                </b-dropdown>

                <b-dropdown class="inner-dropdown" block menu-class="w-100">
                    <template #button-content>
                        <div class="gardner-select">
                            <span>{{ model.te || '-' }}</span>
                            <i class="fa fa-chevron-down select-button" />
                        </div>
                        <span class="dropdown-label">{{ $translate('te') }}</span>
                    </template>
                    <b-dropdown-item
                        v-for="value in teValues"
                        :key="value"
                        href="#"
                        @click="changeValueByKey('te', value)"
                    >
                        {{ value || '-' }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>

            <div class="wrapper action-buttons">
                <b-button pill @click.stop="close">Cancel</b-button>
                <b-button pill class="save" :disabled="!isValid" @click.stop="update">
                    Save
                </b-button>
            </div>
        </b-card>
    </b-collapse>
</template>

<script>
import UIUtils from '../../business/utils/UIUtils'
import BackendProvider from '../../business/services/BackendProvider'
import Vue from 'vue'

export default {
    name: 'EditClinicGardnerDialog',
    props: {
        well: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            expansionValues: [null, '1', '2', '3', '4', '5', '6', 'Col'],
            icmValues: [null, 'A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D'],
            teValues: [null, 'A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D'],
            model: {
                expansion: this.well.clinicGardner ? this.well.clinicGardner.expansion : null,
                icm: this.well.clinicGardner ? this.well.clinicGardner.icm : null,
                te: this.well.clinicGardner ? this.well.clinicGardner.te : null
            }
        }
    },
    computed: {
        isValid() {
            return this.isConsistentValues && this.isChanged
        },
        isConsistentValues() {
            return (
                Object.values(this.model).every(val => val !== null) ||
                Object.values(this.model).every(val => val === null)
            )
        },
        isChanged() {
            const { clinicGardner } = this.well

            const wasEmpty = !clinicGardner
            if (wasEmpty) {
                // assume consistent values: if expansion is not null, all fields are not null
                return !!this.model.expansion
            }
            const { expansion, icm, te } = clinicGardner

            return JSON.stringify({ expansion, icm, te }) !== JSON.stringify(this.model)
        }
    },
    beforeDestroy() {
        this.removeClickListener()
    },
    methods: {
        close() {
            this.$root.$emit('bv::toggle::collapse', this.well._id)
        },
        addClickListener() {
            document.addEventListener('click', this.checkIfOutside)
        },
        checkIfOutside(event) {
            if (!(this.$el === event.target || this.$el.contains(event.target))) {
                this.close()
            }
        },
        removeClickListener() {
            document.removeEventListener('click', this.checkIfOutside)
        },
        changeValueByKey(key, value) {
            this.model[key] = value
        },
        async update() {
            UIUtils.showLoadingMask()
            const isEmpty = Object.values(this.model).every(val => val === null)
            isEmpty ? await this.updateEmptyClinicGardner() : await this.updateClinicGardner()
            this.close()
            UIUtils.hideLoadingMask()
        },
        async updateEmptyClinicGardner() {
            await BackendProvider.updateWell(this.well._id, { clinicGardner: null })
            this.well.clinicGardner = null
        },
        async updateClinicGardner() {
            await BackendProvider.updateWell(this.well._id, { clinicGardner: this.model })
            Vue.set(this.well, 'clinicGardner', { ...this.model })
        }
    }
}
</script>

<style lang="scss">
.edit-clinic-gardner-dialog {
    .card.edit-gardner {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: $dark-6;
        border-radius: 4px;
        padding: 10px;

        .card-body {
            .wrapper {
                display: flex;

                .inner-dropdown {
                    .dropdown-toggle {
                        &::after {
                            display: none;
                        }
                    }

                    ul.dropdown-menu.show {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        min-width: unset;
                        width: 55px !important;
                        background-color: $dark;
                        left: 0 !important;
                        padding: 0;

                        li {
                            width: 100%;
                            :hover {
                                background-color: $dark-6;
                            }
                        }
                        a.dropdown-item {
                            display: flex;
                            justify-content: center;
                            color: $light-3;
                            padding: 5px;
                        }
                    }
                }

                .btn.dropdown-toggle.btn-secondary.btn-block {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-start;
                    width: 55px;
                    height: 45px;
                    border-radius: 3px;
                    background-color: $dark !important;
                    color: $light-3;
                    font-size: 12pt;
                    margin: 0 5px;
                    padding-top: 10px;

                    .dropdown-label {
                        position: relative;
                        font-size: 9pt;
                        color: $light;
                        top: 15px;
                    }

                    .gardner-select {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        .select-button {
                            font-size: 9pt;
                        }
                    }
                }
            }

            .wrapper.action-buttons {
                padding-top: 25px;

                .btn.btn-secondary.rounded-pill {
                    background-color: transparent !important;
                    color: $blue;
                    font-size: 9pt;
                    font-weight: bold !important;
                    padding: 5px 25px;
                    margin: 5px 5px 0 5px;
                }

                .btn.btn-secondary.rounded-pill.save {
                    background-color: $blue !important;
                    color: $dark-6;
                }
            }
        }
    }
}
</style>
